/**
 * /virtual-office — hub for registered-office address, VAT place of business,
 * mail handling, post-incorporation compliance and foreign-owned setup.
 *
 * Hand-authored content from src/data/virtual-office/axes.ts. No AI credits.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { VO_SERVICES, VO_ZONE_AXIS } from "@/data/virtual-office/axes";
import { VO_QA, VO_REVIEWED_TH } from "@/data/virtual-office/questions";
import { AnswerFirst } from "@/components/AnswerFirst";
import { FirmCta } from "@/components/firm/FirmCta";
import { QaHubCta } from "@/components/QaHubCta";
import teamAsset from "@/assets/team-group.png.asset.json";
import voReception from "@/assets/virtual-office-reception.jpg";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  howToSchema,
  faqPageSchema,
} from "@/lib/schema";

const PATH = "/virtual-office";
const TITLE = "Virtual Office จดบริษัทและจด VAT — เอกสาร ขั้นตอน ข้อควรระวัง";
const DESC =
  "ใช้ที่ตั้งสำนักงานแบบบริการจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่มอย่างไรให้เอกสารครบ ผ่านการตรวจสภาพกิจการ และไม่ติดปัญหาที่อยู่ไม่ตรงกันในเอกสารชั้นต่อไป";

const ANSWER =
  "Virtual Office คือการใช้ที่ตั้งสำนักงานที่มีผู้ให้บริการดูแลสถานที่และรับเอกสาร โดยผู้ใช้บริการนำที่อยู่ไปใช้เป็นที่ตั้งสำนักงานของนิติบุคคล ใช้จดทะเบียนบริษัทได้เมื่อระบุที่อยู่ได้ครบถึงเลขห้องและมีหนังสือยินยอมให้ใช้สถานที่พร้อมหลักฐานสิทธิ และใช้จดทะเบียนภาษีมูลค่าเพิ่มได้เมื่อสถานที่ติดป้ายชื่อผู้ประกอบการและมีผู้รับการตรวจสภาพกิจการในเวลาทำการ";

const HUB_FAQ = [
  {
    question: "ใช้ Virtual Office จดทะเบียนภาษีมูลค่าเพิ่มได้หรือไม่?",
    answer:
      "ได้ หากสถานที่นั้นมีหลักฐานสิทธิใช้สถานที่ ติดป้ายแสดงชื่อผู้ประกอบการให้เห็นจากภายนอก มีพื้นที่ใช้งานที่ระบุได้ และมีผู้รับการตรวจสภาพกิจการในเวลาทำการ ผู้ให้บริการบางรายรับเฉพาะการจดทะเบียนบริษัทแต่ไม่รับให้จดทะเบียนภาษีมูลค่าเพิ่ม จึงควรถามให้ชัดและระบุไว้ในสัญญาก่อนชำระเงิน ผลการพิจารณาเป็นอำนาจของเจ้าหน้าที่ผู้ตรวจ",
  },
  {
    question: "เจ้าหน้าที่เข้าตรวจสถานประกอบการดูอะไรบ้าง?",
    answer:
      "โดยทั่วไปดูว่าที่อยู่ตามคำขอตรงกับสถานที่จริง มีป้ายแสดงชื่อผู้ประกอบการที่มองเห็นจากภายนอก มีพื้นที่ที่ใช้ประกอบกิจการหรือเก็บเอกสาร และมีผู้ให้ข้อมูลเกี่ยวกับกิจการอยู่ที่นั้น ควรเตรียมหนังสือรับรองนิติบุคคล สัญญาเช่าหรือหนังสือยินยอมให้ใช้สถานที่ และเอกสารแสดงลักษณะธุรกิจไว้ที่สถานที่",
  },
  {
    question: "ทำไมคำขอจดทะเบียนภาษีมูลค่าเพิ่มจึงถูกให้แก้ไขบ่อย?",
    answer:
      "สาเหตุที่พบมากคือที่อยู่ในคำขอไม่ตรงกับหนังสือรับรองหรือสัญญาแม้เพียงเลขห้อง ไม่มีป้ายแสดงชื่อผู้ประกอบการ ไม่มีผู้รับการตรวจในวันที่เจ้าหน้าที่เข้าตรวจ และหลักฐานสิทธิใช้สถานที่ไม่ครบหรือผู้ให้ความยินยอมไม่ใช่ผู้มีสิทธิจริง",
  },
  {
    question: "ใช้คอนโดหรือบ้านพักเป็นที่ตั้งสำนักงานได้หรือไม่?",
    answer:
      "ในทางทะเบียนทำได้หากเจ้าของยินยอมและมีหลักฐานสิทธิ แต่ต้องตรวจข้อบังคับของนิติบุคคลอาคารชุดก่อน เพราะหลายแห่งกำหนดให้ใช้เพื่ออยู่อาศัยเท่านั้นและห้ามติดป้าย ซึ่งทำให้การจดทะเบียนภาษีมูลค่าเพิ่มยากขึ้นเพราะติดป้ายและรับการตรวจไม่ได้",
  },
  {
    question: "เลิกใช้บริการแล้วต้องทำอะไรกับที่อยู่ที่จดไว้?",
    answer:
      "ต้องย้ายที่ตั้งสำนักงานและสถานประกอบการไปที่อยู่ใหม่ แล้วยื่นจดทะเบียนแก้ไขต่อกรมพัฒนาธุรกิจการค้าและแจ้งเปลี่ยนแปลงต่อกรมสรรพากรก่อนสัญญาสิ้นสุด เพื่อไม่ให้หนังสือราชการที่มีกำหนดเวลาตอบส่งไปยังที่อยู่ที่ไม่มีผู้รับ",
  },
];

export const Route = createFileRoute("/virtual-office/")({
  loader: () => ({
    h: (() => {
      const ogImage = absoluteAsset(teamAsset.url);
      return {
        meta: [
          { title: ctrTitle(TITLE) },
          { name: "description", content: ctrDesc(DESC) },
          { property: "og:title", content: ctrTitle(TITLE) },
          { property: "og:description", content: ctrDesc(DESC) },
          { property: "og:url", content: absoluteUrl(PATH, null, "th") },
          { property: "og:type", content: "website" },
          { property: "og:image", content: ogImage },
          { property: "og:image:alt", content: "ทีมงาน Thai Notary Law & Service" },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:image", content: ogImage },
        ],
        links: [
          { rel: "canonical", href: absoluteUrl(PATH, null, "th") },
          ...canonicalAndHreflang(PATH, null, "th").filter((l) => l.rel === "alternate"),
        ],
        scripts: [
          jsonLdScript(
            buildJsonLd([
              webPageSchema({ url: PATH, title: TITLE, description: DESC }),
              breadcrumbListSchema([
                { name: "หน้าแรก", url: absoluteUrl("/", null, "th") },
                { name: "ที่ตั้งสำนักงานจดทะเบียนบริษัทและจด VAT", url: absoluteUrl(PATH, null, "th") },
              ]),
              ...VO_SERVICES.map((s) =>
                legalServiceSchema({
                  name: s.nameTh,
                  description: s.tagline,
                  url: `${PATH}/${s.slug}`,
                  areaServed: "TH",
                  serviceType: s.nameEn,
                }),
              ),
              howToSchema({
                name: "ลำดับงานจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่มโดยใช้ที่ตั้งสำนักงานแบบบริการ",
                description: DESC,
                steps: VO_SERVICES[1]!.steps.map((t, i) => ({ name: `ขั้นที่ ${i + 1}`, text: t })),
              }),
              faqPageSchema(HUB_FAQ),
            ]),
          ),
        ],
      };
    })(),
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: VirtualOfficeHub,
});

function VirtualOfficeHub() {
  return (
    <>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-14">
          <nav aria-label="เส้นทางหน้า" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/" className="hover:underline">
              หน้าแรก
            </Link>{" "}
            <span aria-hidden>/</span> ที่ตั้งสำนักงานจดทะเบียนบริษัทและจด VAT
          </nav>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight md:text-5xl">
            Virtual Office สำหรับจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม
          </h1>
          <p className="mt-5 max-w-3xl leading-relaxed text-[var(--primary-foreground)]/85">
            {SITE.legalName} ดูแลงานที่ตั้งสำนักงานควบคู่กับงานจดทะเบียนนิติบุคคล งานภาษี และงานบัญชี
            ในสำนวนเดียว เพื่อให้ที่อยู่ที่ใช้จดทะเบียนสอดคล้องกับเอกสารทุกชั้นตั้งแต่หนังสือรับรองนิติบุคคล
            ใบทะเบียนภาษีมูลค่าเพิ่ม ใบกำกับภาษี จนถึงเอกสารที่ธนาคารขอ
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {VO_SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/virtual-office/$service"
                  params={{ service: s.slug }}
                  className="inline-block rounded-full border border-[var(--gold)]/40 px-3 py-1 text-xs font-medium hover:bg-[var(--primary-foreground)]/10"
                >
                  {s.nameTh}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <AnswerFirst>{ANSWER}</AnswerFirst>
        <p className="mt-4 text-xs text-muted-foreground">
          ข้อมูลทบทวน ณ {VO_REVIEWED_TH} · หลักเกณฑ์และเอกสารที่เจ้าหน้าที่เรียกเพิ่มเปลี่ยนแปลงได้
          ควรยืนยันกับกรมพัฒนาธุรกิจการค้าและสำนักงานสรรพากรพื้นที่ที่สถานประกอบการตั้งอยู่ก่อนดำเนินการ ·
          เว็บไซต์ไม่แสดงค่าบริการ ขอใบเสนอราคาเฉพาะเรื่องได้ทางโทรศัพท์ LINE หรืออีเมล
        </p>

        <nav aria-label="สารบัญ" className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="text-base font-semibold text-foreground">หัวข้อในหน้านี้</h2>
          <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            {VO_SERVICES.map((s) => (
              <li key={s.slug}>
                <a href={`#${s.slug}`} className="text-primary hover:underline">
                  {s.nameTh}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {VO_SERVICES.map((s) => (
          <section key={s.slug} id={s.slug} className="scroll-mt-24 border-b border-border/60 py-14">
            <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              <Link to="/virtual-office/$service" params={{ service: s.slug }} className="hover:underline">
                {s.nameTh}
              </Link>
            </h2>
            <p className="mt-2 text-sm font-semibold text-[var(--gold)]">{s.tagline}</p>
            <p data-speakable className="answer-summary mt-4 leading-relaxed text-muted-foreground">
              {s.answer}
            </p>
            <p className="mt-3 text-sm font-semibold">
              <Link
                to="/virtual-office/$service"
                params={{ service: s.slug }}
                className="text-primary hover:underline"
              >
                อ่านรายละเอียด{s.nameTh} — เอกสาร ขั้นตอน เปรียบเทียบทางเลือก และข้อควรระวัง →
              </Link>
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-base font-semibold text-foreground">ขอบเขตงานที่เราทำ</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {s.scope.map((x) => (
                    <li key={x} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">เอกสารที่ต้องเตรียม</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {s.docs.map((x) => (
                    <li key={x} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ))}

        <section className="border-b border-border/60 py-14">
          <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
            เลือกที่ตั้งสำนักงานแบบไหนดี: Virtual Office · สำนักงานเช่า · บ้านหรือคอนโดของกรรมการ
          </h2>
          <figure className="mt-6 overflow-hidden rounded-xl border border-border">
            <img
              src={voReception}
              alt="เคาน์เตอร์รับเอกสารและแผงป้ายชื่อบริษัทในสำนักงานที่ให้บริการที่ตั้งจดทะเบียน"
              width={1280}
              height={720}
              loading="lazy"
              className="h-auto w-full"
            />
            <figcaption className="bg-card px-4 py-2 text-xs text-muted-foreground">
              ภาพประกอบ: จุดรับเอกสารและแผงป้ายชื่อผู้ประกอบการ ซึ่งเป็นสองสิ่งที่เจ้าหน้าที่มักดูเมื่อตรวจสภาพกิจการ
            </figcaption>
          </figure>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-foreground">
                  <th className="py-2 pr-4">ประเด็น</th>
                  <th className="py-2 pr-4">Virtual Office</th>
                  <th className="py-2 pr-4">สำนักงานเช่า</th>
                  <th className="py-2">บ้าน/คอนโดกรรมการ</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {[
                  ["หลักฐานสิทธิใช้สถานที่", "สัญญาบริการ + หนังสือยินยอมจากผู้ให้บริการ", "สัญญาเช่า + สำเนาทะเบียนบ้านของอาคาร", "หนังสือยินยอมเจ้าบ้าน + สำเนาทะเบียนบ้าน"],
                  ["การติดป้ายชื่อ", "ขึ้นกับผู้ให้บริการ ควรระบุในสัญญา", "ติดได้ตามข้อตกลงกับผู้ให้เช่า", "คอนโดหลายแห่งห้ามตามข้อบังคับอาคาร"],
                  ["ผู้รับการตรวจสภาพกิจการ", "พนักงานต้อนรับของผู้ให้บริการ (ถ้าตกลงไว้)", "พนักงานของบริษัทเอง", "ต้องมีคนอยู่ในวันตรวจ"],
                  ["การรับหนังสือราชการ", "ผู้ให้บริการรับและแจ้งต่อ", "รับเองที่สำนักงาน", "รับเองที่บ้าน"],
                  ["เหมาะกับ", "ธุรกิจออนไลน์ บริการ ที่ปรึกษา ช่วงเริ่มต้น", "ธุรกิจที่มีพนักงานหรือสต็อกสินค้า", "ธุรกิจครอบครัวขนาดเล็ก"],
                ].map((r) => (
                  <tr key={r[0]} className="border-b border-border/60 align-top">
                    <td className="py-3 pr-4 font-medium text-foreground">{r[0]}</td>
                    <td className="py-3 pr-4">{r[1]}</td>
                    <td className="py-3 pr-4">{r[2]}</td>
                    <td className="py-3">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="mt-10 text-lg font-semibold text-foreground">เมื่อไรต้องจดทะเบียนภาษีมูลค่าเพิ่ม</h3>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            ตามประมวลรัษฎากร ผู้ประกอบการที่ขายสินค้าหรือให้บริการโดยมีรายรับเกิน 1.8 ล้านบาทต่อปี
            ต้องยื่นคำขอจดทะเบียนภาษีมูลค่าเพิ่ม (แบบ ภ.พ.01) ภายใน 30 วันนับแต่วันที่รายรับเกิน
            ส่วนผู้ที่รายรับยังไม่ถึงเกณฑ์จะขอจดทะเบียนโดยสมัครใจก็ได้ เช่น เมื่อคู่ค้าต้องการใบกำกับภาษี
            การยื่นคำขอใช้ที่อยู่เดียวกับที่ตั้งสถานประกอบการ จึงควรตกลงกับผู้ให้บริการ Virtual Office
            เรื่องป้ายชื่อและผู้รับการตรวจไว้ตั้งแต่ก่อนจดทะเบียนบริษัท
          </p>
          <h3 className="mt-8 text-lg font-semibold text-foreground">เช็กลิสต์ก่อนวันที่เจ้าหน้าที่เข้าตรวจ</h3>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>ป้ายชื่อบริษัทติดอยู่และอ่านได้จากภายนอก ชื่อตรงกับหนังสือรับรองนิติบุคคล</li>
            <li>เลขห้อง ชั้น และอาคารในคำขอตรงกับสัญญาและหนังสือยินยอมทุกตัวอักษร</li>
            <li>มีสำเนาหนังสือรับรอง สัญญา และเอกสารลักษณะธุรกิจเก็บไว้ที่สถานที่</li>
            <li>แจ้งผู้รับการตรวจล่วงหน้าว่าธุรกิจทำอะไร ขายให้ใคร และใครเป็นผู้ติดต่อ</li>
            <li>เก็บเบอร์โทรของกรรมการไว้ให้เจ้าหน้าที่ติดต่อได้ในวันตรวจ</li>
          </ol>
        </section>

        <section className="py-12">
          <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">พื้นที่ที่ลูกค้าถามถึงมากที่สุด</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            ทีมงานรับเรื่องได้ทุกจังหวัด โดยเอกสารส่งตรวจล่วงหน้าทาง LINE หรืออีเมลได้ พื้นที่ที่มีคำถามเข้ามามากคือ
            {" "}
            {VO_ZONE_AXIS.map((z) => z.th).join(" · ")}
          </p>
        </section>

        <section className="pb-12">
          <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">คำถามที่พบบ่อย</h2>
          <dl className="mt-6 space-y-6">
            {HUB_FAQ.map((f) => (
              <div key={f.question} className="rounded-xl border border-border bg-card p-6">
                <dt className="font-semibold text-foreground">{f.question}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="pb-12">
          <h2 className="text-xl font-bold text-foreground">แหล่งข้อมูลราชการที่ตรวจสอบได้</h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <a
                href="https://www.dbd.go.th/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                กรมพัฒนาธุรกิจการค้า
              </a>{" "}
              — ตรวจขั้นตอนและแบบพิมพ์การจดทะเบียนนิติบุคคลและการแก้ไขที่ตั้งสำนักงาน
            </li>
            <li>
              <a
                href="https://www.rd.go.th/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                กรมสรรพากร
              </a>{" "}
              — ตรวจเงื่อนไขการจดทะเบียนภาษีมูลค่าเพิ่มและรูปแบบใบกำกับภาษี
            </li>
            <li>
              <a
                href="https://www.rd.go.th/5937.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                ประมวลรัษฎากร หมวด 4 ภาษีมูลค่าเพิ่ม
              </a>{" "}
              — ตัวบทเรื่องการจดทะเบียนภาษีมูลค่าเพิ่ม (มาตรา 85 และ 85/1)
            </li>
          </ul>
        </section>
      </div>

      <QaHubCta base="/virtual-office" label="ที่ตั้งสำนักงาน จดบริษัท และจดทะเบียนภาษีมูลค่าเพิ่ม" qa={VO_QA} />
      <FirmCta
        heading="ปรึกษาเรื่องที่ตั้งสำนักงานและการจด VAT"
        body="เล่าลักษณะธุรกิจและพื้นที่ที่ต้องการใช้ ทีมงานจะแจ้งเอกสารที่ต้องเตรียม ลำดับงาน และจุดที่มักถูกให้แก้ไข ก่อนเริ่มงานจะแจ้งขอบเขตงานและค่าบริการเป็นลายลักษณ์อักษร"
      />
    </>
  );
}
